import { type ComponentType, lazy } from 'react';
import { Navigate } from 'react-router-dom';

import { ALL_PERMISSIONS } from '@/features/roles/permission-groups';

import { useAuthStore } from './auth-store';
import { usePermission } from './usePermission';

const REAL_SLUGS = new Set(ALL_PERMISSIONS.map((s) => s.toLowerCase()));

interface PageModule {
  default: ComponentType<unknown>;
  permission?: string | string[];
}

/**
 * Lazy-load a page and enforce its exported `permission`.
 *
 * Safety rules so legitimate users are never locked out by mismatched exports:
 * - Only enforce when the exported permission is a REAL backend slug
 *   (placeholder/typo exports like `Reports_view` are ignored).
 * - Matching is case-insensitive (backend mixes `Pos_view` and `pos_view`).
 * - While permissions haven't hydrated yet (empty set), access is allowed.
 */
export function guardedLazy(factory: () => Promise<PageModule>) {
  return lazy(async () => {
    const mod = await factory();
    const Page = mod.default;
    const perm = mod.permission;
    const names = (perm == null ? [] : Array.isArray(perm) ? perm : [perm]).filter((n) =>
      REAL_SLUGS.has(n.toLowerCase()),
    );
    if (names.length === 0) return { default: Page };

    const Guarded: ComponentType<Record<string, unknown>> = (props) => {
      const size = useAuthStore((s) => s.permissions.size);
      const allowed = usePermission(names);
      if (size > 0 && !allowed) return <Navigate to="/403" replace />;
      return <Page {...props} />;
    };
    return { default: Guarded as ComponentType<unknown> };
  });
}
